import React, { FC, MouseEvent, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';

type ColumnResizeHandleProps = {
    initialWidth: number;
    minWidth: number;
    maxWidth: number;
    onResize: (width: number) => void;
    onResizeEnd: (width: number) => void;
};

const ColumnResizeHandle: FC<ColumnResizeHandleProps> = ({
    initialWidth,
    minWidth,
    maxWidth,
    onResize,
    onResizeEnd,
}) => {
    const [isDragging, setIsDragging] = useState(false);
    const dragStateRef = useRef<{ startX: number; startWidth: number; lastWidth: number } | null>(null);

    useEffect(() => {
        if (!isDragging) return;

        const handleMouseMove = (event: globalThis.MouseEvent) => {
            const state = dragStateRef.current;
            if (!state) return;
            const delta = event.clientX - state.startX;
            const next = Math.min(Math.max(state.startWidth + delta, minWidth), maxWidth);
            state.lastWidth = next;
            onResize(next);
        };

        const handleMouseUp = () => {
            const state = dragStateRef.current;
            dragStateRef.current = null;
            setIsDragging(false);
            if (state) {
                onResizeEnd(state.lastWidth);
            }
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        const previousUserSelect = document.body.style.userSelect;
        const previousCursor = document.body.style.cursor;
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'col-resize';

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.userSelect = previousUserSelect;
            document.body.style.cursor = previousCursor;
        };
    }, [isDragging, minWidth, maxWidth, onResize, onResizeEnd]);

    const handleMouseDown = (event: MouseEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        dragStateRef.current = {
            startX: event.clientX,
            startWidth: initialWidth,
            lastWidth: initialWidth,
        };
        setIsDragging(true);
    };

    return (
        <Box
            onMouseDown={handleMouseDown}
            onClick={(event) => event.stopPropagation()}
            sx={{
                position: 'absolute',
                top: 6,
                bottom: 6,
                right: 2,
                width: '2px',
                cursor: 'col-resize',
                userSelect: 'none',
                touchAction: 'none',
                zIndex: 1,
                backgroundColor: 'grey.200',
            }}
        />
    );
};

export default ColumnResizeHandle;
